import { defineStore } from "pinia";
const STORAGE_KEY = "mc_año_activo";
export const normalizeYear = (year) => String(year ?? "").trim();
export const sameYear = (a, b) => {
    const left = normalizeYear(a);
    const right = normalizeYear(b);
    return Boolean(left && right && left === right);
};
export const yearsFromCourses = (cursos) => {
    const years = Array.from(new Set((cursos || [])
        .map((curso) => normalizeYear(curso.year))
        .filter(Boolean))).sort((a, b) => b.localeCompare(a));
    return years;
};
export const filterCoursesByYear = (cursos, year) => {
    const target = normalizeYear(year);
    if (!target)
        return [];
    return (cursos || []).filter((curso) => sameYear(curso.year, target));
};
const readStoredYear = () => {
    if (typeof window === "undefined")
        return null;
    const stored = normalizeYear(window.localStorage.getItem(STORAGE_KEY));
    return stored || null;
};
const persistYear = (año) => {
    if (typeof window === "undefined")
        return;
    if (año) {
        window.localStorage.setItem(STORAGE_KEY, año);
        return;
    }
    window.localStorage.removeItem(STORAGE_KEY);
};
export const useYearStore = defineStore("year", {
    state: () => ({
        añoActivo: readStoredYear(),
        añosDisponibles: [],
    }),
    actions: {
        calcularAñosDisponibles(cursos) {
            this.añosDisponibles = yearsFromCourses(cursos);
            return this.añosDisponibles;
        },
        /**
         * Garantiza un año activo válido a partir de los cursos.
         * Nunca deja la UI sin año si hay cursos (evita listar todos los años).
         */
        ensureAñoActivo(cursos, preferredYear) {
            const años = this.calcularAñosDisponibles(cursos);
            if (años.length === 0) {
                this.añoActivo = null;
                persistYear(null);
                return null;
            }
            const actual = normalizeYear(this.añoActivo);
            if (actual && años.includes(actual)) {
                return actual;
            }
            const preferred = normalizeYear(preferredYear);
            if (preferred && años.includes(preferred)) {
                this.añoActivo = preferred;
                persistYear(preferred);
                return preferred;
            }
            const stored = readStoredYear();
            if (stored && años.includes(stored)) {
                this.añoActivo = stored;
                return stored;
            }
            // Año más reciente disponible.
            this.añoActivo = años[0];
            persistYear(this.añoActivo);
            return this.añoActivo;
        },
        /** Compat: primera inicialización / re-sync desde MainView. */
        inicializarAñoActivo(cursos) {
            this.ensureAñoActivo(cursos);
        },
        setAñoActivo(año) {
            const normalized = normalizeYear(año);
            this.añoActivo = normalized || null;
            persistYear(this.añoActivo);
        },
    },
    getters: {
        esAñoActual(state) {
            const añoActual = new Date().getFullYear().toString();
            return state.añoActivo === añoActual;
        },
    },
});
